import Logger from '../lib/logger';

// -----------------------------------------------------------------------------
// CartGifts Web Component
// -----------------------------------------------------------------------------
// Gift With Purchase module. Configuration is supplied via theme settings
// (snippets/cart-gifts.liquid) as JSON inside the custom element.

/**
 * Trigger modes for Gift With Purchase
 * @readonly
 * @enum {string}
 */
const GWP_MODES = {
	THRESHOLD: 'threshold',
	PRODUCT_IN_CART: 'productInCart',
};

/**
 * Per-gift display states
 * @readonly
 * @enum {string}
 */
const GIFT_STATES = {
	UNLOCKED: 'unlocked',
	MISSED: 'missed',
	ACTIVE: 'active',
	LOCKED: 'locked',
};

const GIFT_BADGE_CHECKMARK = `<svg class="cart-gifts__item__badge__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="m1.88 11.988 6.21 6.103L22.467 3.83" stroke="currentColor" stroke-width="3.055"/></svg>`;

function variantIdMatches(a, b) {
	if (a == null || b == null) return false;
	return Number(a) === Number(b);
}

/** Whether a product.js variant can be sold right now. */
function isVariantInStock(variant) {
	if (!variant) return false;
	if (variant.available === false) return false;
	const managed = variant.inventory_management != null && variant.inventory_management !== '';
	if (managed && variant.inventory_quantity <= 0) return false;
	return true;
}

/** Resolve the configured gift variant from product.js data. */
function findGiftVariant(data, giftVariantId) {
	if (!data?.variants?.length) return null;
	if (giftVariantId) {
		const match = data.variants.find(v => variantIdMatches(v.id, giftVariantId));
		if (match) return match;
	}
	return data.variants.length === 1 ? data.variants[0] : null;
}

/**
 * Read GWP config injected by Liquid theme settings.
 * @param {HTMLElement} rootEl
 */
function readCartGiftsConfig(rootEl) {
	const script = rootEl.querySelector('[data-cart-gifts-config]');
	if (!script) return { enabled: false, gifts: [] };
	try {
		return JSON.parse(script.textContent);
	} catch (err) {
		Logger.error('CartGifts > readCartGiftsConfig', err);
		return { enabled: false, gifts: [] };
	}
}

/**
 * Normalize theme-setting gift config into Gift constructor shape.
 * @param {object} cfg
 */
function normalizeGiftConfig(cfg) {
	const mode = cfg.mode === 'product_in_cart' || cfg.mode === GWP_MODES.PRODUCT_IN_CART
		? GWP_MODES.PRODUCT_IN_CART
		: GWP_MODES.THRESHOLD;

	let triggerVariantId = cfg.triggerVariantId ?? null;
	if (Array.isArray(triggerVariantId)) {
		triggerVariantId = triggerVariantId.filter(id => id);
		if (triggerVariantId.length === 0) triggerVariantId = null;
	}

	return {
		handle: typeof cfg.handle === 'string' ? cfg.handle : '',
		title: cfg.title || '',
		giftVariantId: cfg.giftVariantId ? Number(cfg.giftVariantId) : null,
		featuredImage: cfg.featuredImage || '',
		available: cfg.available !== false,
		mode,
		triggerVariantId,
		threshold: cfg.threshold ?? null,
		enabled: cfg.enabled !== false,
		triggerProductTitle: cfg.triggerProductTitle || '',
	};
}

/**
 * Represents a Gift With Purchase product and its eligibility logic.
 */
class Gift {
	/**
	 * @param {object} config
	 */
	constructor({
		handle,
		mode,
		triggerVariantId = null,
		threshold = null,
		enabled = true,
		triggerProductTitle = '',
		title = '',
		giftVariantId = null,
		featuredImage = '',
		available = true,
	}) {
		this.handle = handle;
		this.mode = mode;
		this.triggerVariantId = triggerVariantId;
		this.threshold = threshold;
		this.configEnabled = enabled;
		this.triggerProductTitle = triggerProductTitle;
		this.title = title;
		this.giftVariantId = giftVariantId;
		this.featuredImage = featuredImage;
		this.available = available;
		this.enabled = false;
		this.loadError = false;
		this.inCart = false;
		this._prefilled = Boolean(title && giftVariantId);
	}

	isConfigValid() {
		return (
			(this.mode === GWP_MODES.THRESHOLD && this.threshold != null && this.threshold >= 0) ||
			(this.mode === GWP_MODES.PRODUCT_IN_CART && (
				Array.isArray(this.triggerVariantId)
					? this.triggerVariantId.length > 0
					: this.triggerVariantId
			))
		);
	}

	/** Cart has at least one line that is not this gift variant. */
	hasQualifyingCartItems(cartData) {
		if (!cartData?.items?.length) return false;
		return cartData.items.some(item => !this.isGiftCartLine(item));
	}

	/** Whether a cart line belongs to this gift product. */
	isGiftCartLine(item) {
		if (!item) return false;
		if (
			this.giftVariantId
			&& (
				variantIdMatches(item.variant_id, this.giftVariantId)
				|| variantIdMatches(item.id, this.giftVariantId)
			)
		) {
			return true;
		}
		return Boolean(this.handle && item.handle === this.handle);
	}

	/** All cart lines for this gift (normally zero or one). */
	getGiftCartLines(cartData) {
		if (!cartData?.items) return [];
		return cartData.items.filter(item => this.isGiftCartLine(item));
	}

	/** Variant id to pass to /cart/update.js for a matched line. */
	getLineVariantId(line) {
		return line?.variant_id ?? line?.id ?? this.giftVariantId;
	}

	applyFetchedProduct(data) {
		this.title = data.title;
		this.variants = data.variants;
		const variant = findGiftVariant(data, this.giftVariantId);
		if (variant) {
			this.giftVariantId = variant.id;
			this.available = isVariantInStock(variant);
		}
		if (data.featured_image) this.featuredImage = data.featured_image;
		if (this.mode === GWP_MODES.THRESHOLD && (this.threshold == null || this.threshold === '')) {
			this.threshold = data.threshold || 0;
		}
	}

	/**
	 * Load product data and set config validity.
	 */
	async load() {
		if (this._prefilled) {
			this.enabled = this.configEnabled && this.isConfigValid() && this.giftVariantId;
			this.loadError = false;
		}

		try {
			const url = `${window.Shopify.routes.root}products/${this.handle}.js`;
			const resp = await fetch(url);
			if (!resp.ok) {
				if (this._prefilled) return;
				throw new Error(`Failed to load product: ${this.handle}`);
			}
			const data = await resp.json();
			this.applyFetchedProduct(data);
			this.enabled = this.configEnabled && this.isConfigValid() && this.giftVariantId;
			this.loadError = false;
		} catch (err) {
			if (this._prefilled) {
				Logger.info('CartGifts > Gift.load > using theme product data', this.handle);
				return;
			}
			Logger.error('CartGifts > Gift.load', err);
			this.loadError = true;
			this.enabled = false;
		}
	}

	/**
	 * Refresh stock availability without reloading full product data.
	 */
	async reloadStock() {
		if (!this.handle) return;
		try {
			const url = `${window.Shopify.routes.root}products/${this.handle}.js`;
			const resp = await fetch(url);
			if (!resp.ok) return;
			const data = await resp.json();
			const variant = findGiftVariant(data, this.giftVariantId);
			if (variant) this.available = isVariantInStock(variant);
		} catch (err) {
			Logger.info('CartGifts > Gift.reloadStock > keeping cached availability', this.handle);
		}
	}

	/**
	 * Line item for this gift variant in the cart, if present.
	 */
	getGiftCartLine(cartData) {
		const lines = this.getGiftCartLines(cartData);
		return lines[0] || null;
	}

	/**
	 * Update inCart status based on cart data.
	 */
	updateCartStatus(cartData) {
		this.inCart = Boolean(this.getGiftCartLine(cartData));
	}

	/**
	 * Check eligibility from cart rules (ignores enabled flag).
	 */
	wouldBeEligible(cartData) {
		if (!cartData) return false;
		if (this.mode === GWP_MODES.THRESHOLD) {
			if (this.threshold == null || this.threshold < 0) return false;
			if (!this.hasQualifyingCartItems(cartData)) return false;
			if (this.threshold === 0) return true;
			return cartData.original_total_price >= this.threshold;
		}
		if (this.mode === GWP_MODES.PRODUCT_IN_CART) {
			const matchesTrigger = id => cartData.items.some(
				item => variantIdMatches(item.id, id) || variantIdMatches(item.variant_id, id)
			);
			if (Array.isArray(this.triggerVariantId)) {
				return this.triggerVariantId.some(id => matchesTrigger(id));
			}
			return matchesTrigger(this.triggerVariantId);
		}
		return false;
	}

	/**
	 * Check if the gift is eligible based on trigger mode.
	 */
	isEligible(cartData) {
		if (!this.giftVariantId || !this.isConfigValid()) return false;
		if (!this.enabled && !this._prefilled) return false;
		return this.wouldBeEligible(cartData);
	}

	/**
	 * UI state including configured gifts that failed to load.
	 */
	getDisplayState() {
		if (!this.configEnabled || !this.handle) return null;
		if (!this.available) return GIFT_STATES.MISSED;
		if (!this.giftVariantId) return GIFT_STATES.LOCKED;
		if (this.inCart) return GIFT_STATES.UNLOCKED;
		if (this.loadError || !this.enabled) return GIFT_STATES.LOCKED;
		if (this.isEligible(this._cartData)) return GIFT_STATES.ACTIVE;
		return GIFT_STATES.LOCKED;
	}

	/** @param {object} cartData */
	setCartContext(cartData) {
		this._cartData = cartData;
	}

	getDisplayTitle() {
		if (this.title) return this.title.replace(' (Free Gift)', '');
		if (this.handle) return this.handle.replace(/-/g, ' ');
		return 'Free gift';
	}

	getImageUrl() {
		if (!this.featuredImage) return '';
		if (this.featuredImage.startsWith('http') || this.featuredImage.startsWith('//')) {
			return this.featuredImage;
		}
		return `${this.featuredImage}&width=126`;
	}
}

/**
 * Core logic for CartGifts: inject UI, monitor cart, and sync gifts.
 */
class CartGiftsLogic {
	constructor(rootEl) {
		this.container = rootEl;
		this.gwpEnabled = false;
		this.gifts = [];
		this.cartData = null;
		this.headlineEl = null;
		this.meterEl = null;
		this.meterLineEl = null;
		this.itemsEl = null;
		this._syncing = false;
		this._bannerUiSignature = '';
		this._pollStockTick = 0;
	}

	/**
	 * Load config from theme settings JSON embedded in the element.
	 */
	loadConfig() {
		const config = readCartGiftsConfig(this.container);
		this.gwpEnabled = Boolean(config.enabled);
		this.gifts = (config.gifts || []).map(cfg => new Gift(normalizeGiftConfig(cfg)));
	}

	/**
	 * Inject banner markup without removing the theme settings config script.
	 */
	ensureBannerDOM() {
		if (this.container.querySelector('.cart-gifts__banner')) {
			this.cacheBannerElements();
			return;
		}

		const configScript = this.container.querySelector('[data-cart-gifts-config]');
		const banner = document.createElement('div');
		banner.className = 'cart-gifts__banner';
		banner.innerHTML = `
          <h3 class="cart-gifts__headline"></h3>
          <div class="cart-gifts__meter" hidden>
            <div class="cart-gifts__meter__line"></div>
          </div>
          <div class="cart-gifts__items"></div>`;

		if (configScript) {
			configScript.insertAdjacentElement('afterend', banner);
		} else {
			this.container.appendChild(banner);
		}

		this.cacheBannerElements();
	}

	cacheBannerElements() {
		this.headlineEl = this.container.querySelector('.cart-gifts__headline');
		this.meterEl = this.container.querySelector('.cart-gifts__meter');
		this.meterLineEl = this.container.querySelector('.cart-gifts__meter__line');
		this.itemsEl = this.container.querySelector('.cart-gifts__items');
	}

	/**
	 * Initialize component: handle global toggle, load gifts, sync cart.
	 */
	async init() {
		if (!this.gwpEnabled) {
			this.container.style.display = 'none';
			await Promise.all(this.gifts.map(g => g.load()));
			this.cartData = await this.fetchCart();
			if (this.cartData) {
				const updates = {};
				this.gifts.forEach(g => {
					g.updateCartStatus(this.cartData);
					g.getGiftCartLines(this.cartData).forEach(line => {
						updates[g.getLineVariantId(line)] = 0;
					});
				});
				if (Object.keys(updates).length) {
					await fetch('/cart/update.js', {
						method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates })
					});
				}
			}
			return;
		}

		await Promise.all(this.gifts.map(g => g.load()));
		await this.onCartChange();
	}

	/** Fetch current cart data. */
	async fetchCart() {
		const resp = await fetch('/cart.js');
		return resp.ok ? resp.json() : null;
	}

	/**
	 * Handle cart updates: refresh stock, UI, and sync gifts.
	 * @param {object} [cartFromEvent]
	 */
	async onCartChange(cartFromEvent = null, { refreshStock = true } = {}) {
		if (!this.gwpEnabled || this._syncing) return;
		if (refreshStock) {
			await Promise.all(this.gifts.map(g => g.reloadStock()));
		}
		this.cartData = cartFromEvent || await this.fetchCart();
		if (!this.cartData) return;
		this.gifts.forEach(g => {
			g.setCartContext(this.cartData);
			g.updateCartStatus(this.cartData);
		});
		this.renderBanner();
		await this.syncCart();
	}

	/**
	 * Gifts configured in theme settings (show banner even if product fetch fails).
	 */
	getConfiguredGifts() {
		return this.gifts.filter(g => g.configEnabled && g.handle);
	}

	/**
	 * Gift to drive headline + progress meter (locked threshold first, then other locked/active).
	 */
	getFocusGift(enabledGifts) {
		const byPriority = [
			...enabledGifts.filter(g => g.getDisplayState() === GIFT_STATES.ACTIVE),
			...enabledGifts.filter(g => g.getDisplayState() === GIFT_STATES.LOCKED),
		];
		return byPriority[0] || null;
	}

	/**
	 * Row copy for each gift state.
	 */
	getGiftRowContent(gift, state) {
		if (gift.loadError) {
			return {
				badge: 'Unavailable',
				instructions: 'Gift product could not be loaded — check theme settings.',
			};
		}

		switch (state) {
			case GIFT_STATES.UNLOCKED:
				return {
					badge: 'Added',
					badgeModifier: 'added',
					badgeHasCheckmark: true,
					instructions: 'Added to your order!',
				};
			case GIFT_STATES.MISSED:
				return {
					badge: 'Sold out',
					badgeModifier: 'sold-out',
					instructions: 'This giveaway has ended — next time, don\'t wait!',
				};
			case GIFT_STATES.ACTIVE:
				return {
					badge: '',
					badgeIsCheckmark: true,
					instructions: 'Adding to your order…',
				};
			case GIFT_STATES.LOCKED:
				if (gift.mode === GWP_MODES.THRESHOLD) {
					if (gift.threshold === 0) {
						return {
							badge: 'Locked',
							instructions: 'Add an item to your order to unlock this gift.',
						};
					}
					const threshold = (gift.threshold / 100).toFixed(0);
					return {
						badge: 'Locked',
						instructions: `Spend $${threshold} to unlock this gift.`,
					};
				}
				return {
					badge: 'Locked',
					instructions: `Add ${gift.triggerProductTitle} to unlock this gift.`,
				};
			default:
				return { badge: '', instructions: '' };
		}
	}

	/**
	 * Build aggregate headline from gift states.
	 */
	buildHeadline(enabledGifts) {
		const states = enabledGifts.map(g => g.getDisplayState());
		const total = enabledGifts.length;
		const unlocked = states.filter(s => s === GIFT_STATES.UNLOCKED).length;
		const missed = states.filter(s => s === GIFT_STATES.MISSED).length;
		const focus = this.getFocusGift(enabledGifts);

		if (unlocked === total) {
			return total === 1
				? 'You\'ve unlocked your free gift!'
				: `You've unlocked all ${total} free gifts!`;
		}

		if (missed === total) {
			return total === 1
				? 'You missed this free gift — it\'s sold out!'
				: 'These free gifts have ended — you missed out this time.';
		}

		if (unlocked > 0 && missed > 0 && !focus) {
			const giftWord = unlocked > 1 ? 'gifts' : 'gift';
			const missedWord = missed > 1 ? 'giveaways have' : 'giveaway has';
			return `You got ${unlocked} free ${giftWord}! ${missed > 1 ? 'Some' : 'One'} ${missedWord} ended.`;
		}

		if (!focus) {
			return unlocked > 0
				? 'You\'ve unlocked your free gift!'
				: 'Free gift available with your order!';
		}

		const title = focus.getDisplayTitle();
		const prefix = unlocked
			? `You've unlocked ${unlocked} gift${unlocked > 1 ? 's' : ''}! Next up: `
			: '';

		let headline = '';

		if (focus.getDisplayState() === GIFT_STATES.ACTIVE || focus.isEligible(this.cartData)) {
			headline = `${prefix}You've unlocked a free ${title}!`;
		} else if (focus.mode === GWP_MODES.THRESHOLD) {
			if (focus.threshold === 0) {
				headline = `${prefix}Add an item to get a free ${title}!`;
			} else {
				const spent = this.cartData.original_total_price;
				const remainingCents = Math.max(0, focus.threshold - spent);
				const remaining = (remainingCents / 100).toFixed(2);
				headline = `${prefix}You're $${remaining} away from a free ${title}!`;
			}
		} else {
			headline = `${prefix}Get a free ${title} with your order!`;
		}

		if (missed > 0 && unlocked === 0) {
			const endedNote = missed > 1
				? ' Other giveaways have already ended.'
				: ' Another giveaway has already ended.';
			headline += endedNote;
		}

		return headline;
	}

	/**
	 * Progress meter width for the focus gift (empty when hidden).
	 */
	getMeterPercent(focusGift) {
		if (!focusGift || !this.cartData) return '';
		if (focusGift.mode !== GWP_MODES.THRESHOLD) return '';

		const state = focusGift.getDisplayState();
		const showMeter = state === GIFT_STATES.LOCKED || state === GIFT_STATES.ACTIVE;
		if (!showMeter) return '';

		const spent = this.cartData.original_total_price;
		const threshold = focusGift.threshold;
		if (state === GIFT_STATES.ACTIVE || focusGift.isEligible(this.cartData)) {
			return '100%';
		}
		if (threshold === 0) return '0%';
		return `${Math.min((spent / threshold) * 100, 100)}%`;
	}

	/** Stable key for a gift row's visible content — skip DOM rebuild when unchanged. */
	getGiftRowSignature(gift) {
		const state = gift.getDisplayState();
		if (!state) return '';

		const {
			badge,
			badgeIsCheckmark,
			badgeModifier,
			badgeHasCheckmark,
			instructions,
		} = this.getGiftRowContent(gift, state);

		return [
			gift.handle,
			state,
			gift.getDisplayTitle(),
			gift.getImageUrl(),
			badge,
			badgeModifier || '',
			badgeHasCheckmark ? '1' : '0',
			badgeIsCheckmark ? '1' : '0',
			instructions,
		].join('\u0001');
	}

	/** Stable key for the full banner UI — avoids re-rendering images on poll ticks. */
	getBannerUiSignature(configuredGifts, headlineGifts) {
		const states = configuredGifts.map(g => g.getDisplayState()).filter(Boolean);
		const allMissed = states.length > 0 && states.every(s => s === GIFT_STATES.MISSED);
		const allUnlocked = states.length > 0 && states.every(s => s === GIFT_STATES.UNLOCKED);
		const bannerState = allMissed ? 'all-missed' : allUnlocked ? 'all-unlocked' : 'mixed';
		const focusGift = this.getFocusGift(headlineGifts);
		const rows = configuredGifts.map(g => this.getGiftRowSignature(g)).join('\u0002');

		return [
			bannerState,
			String(configuredGifts.length),
			this.buildHeadline(headlineGifts),
			this.getMeterPercent(focusGift),
			rows,
		].join('\u0003');
	}

	/**
	 * Update progress meter for threshold gifts.
	 */
	updateMeter(focusGift) {
		if (!this.meterEl || !this.meterLineEl) return;

		const showMeter = focusGift
			&& focusGift.mode === GWP_MODES.THRESHOLD
			&& (focusGift.getDisplayState() === GIFT_STATES.LOCKED || focusGift.getDisplayState() === GIFT_STATES.ACTIVE);

		this.meterEl.hidden = !showMeter;
		if (!showMeter) return;

		this.meterLineEl.style.width = this.getMeterPercent(focusGift);
	}

	/**
	 * Render per-gift rows and headline.
	 */
	renderGiftRows(displayGifts) {
		if (!this.itemsEl) return;

		this.itemsEl.innerHTML = displayGifts.map(gift => {
			const state = gift.getDisplayState();
			if (!state) return '';
			const { badge, badgeIsCheckmark, badgeModifier, badgeHasCheckmark, instructions } = this.getGiftRowContent(gift, state);
			const title = gift.getDisplayTitle();
			const imgUrl = gift.getImageUrl();
			const badgeClass = badgeModifier
				? `cart-gifts__item__badge cart-gifts__item__badge--${badgeModifier}`
				: 'cart-gifts__item__badge';
			let badgeHtml;
			if (badgeHasCheckmark) {
				badgeHtml = `<div class="${badgeClass}"><span>${badge}</span>${GIFT_BADGE_CHECKMARK}</div>`;
			} else if (badgeIsCheckmark) {
				badgeHtml = `<div class="cart-gifts__item__badge cart-gifts__item__badge--check" aria-label="Unlocked">${GIFT_BADGE_CHECKMARK}</div>`;
			} else {
				badgeHtml = `<div class="${badgeClass}">${badge}</div>`;
			}

			return `
        <div class="cart-gifts__item cart-gifts__item--${state}" data-gift-handle="${gift.handle}">
          <div class="cart-gifts__item__image">
            <img src="${imgUrl}" alt="" decoding="async" />
          </div>
          <div class="cart-gifts__item__content">
            <div class="cart-gifts__item__title">
              <h4>${title}</h4>
              <p>${instructions}</p>
            </div>
            ${badgeHtml}
          </div>
        </div>`;
		}).join('');
	}

	/**
	 * Render banner state: per-gift rows, headline, and meter.
	 */
	renderBanner() {
		if (!this.gwpEnabled) {
			this.container.style.display = 'none';
			return;
		}

		const configuredGifts = this.getConfiguredGifts();
		if (configuredGifts.length === 0) {
			this.container.style.display = 'none';
			this._bannerUiSignature = '';
			const banner = this.container.querySelector('.cart-gifts__banner');
			if (banner) banner.remove();
			return;
		}

		const activeGifts = configuredGifts.filter(g => g.enabled);
		const headlineGifts = activeGifts.length ? activeGifts : configuredGifts;

		this.ensureBannerDOM();

		const uiSignature = this.getBannerUiSignature(configuredGifts, headlineGifts);
		if (uiSignature === this._bannerUiSignature) {
			this.container.style.display = '';
			return;
		}
		this._bannerUiSignature = uiSignature;

		const states = configuredGifts.map(g => g.getDisplayState()).filter(Boolean);
		const allMissed = states.length > 0 && states.every(s => s === GIFT_STATES.MISSED);
		const allUnlocked = states.length > 0 && states.every(s => s === GIFT_STATES.UNLOCKED);

		this.container.dataset.bannerState = allMissed ? 'all-missed' : allUnlocked ? 'all-unlocked' : 'mixed';
		this.container.dataset.numGifts = String(configuredGifts.length);

		if (this.headlineEl) {
			this.headlineEl.textContent = this.buildHeadline(headlineGifts);
		}
		this.renderGiftRows(configuredGifts);
		this.updateMeter(this.getFocusGift(headlineGifts));
		this.container.style.display = '';
	}

	/**
	 * Remove gift lines via line-item key when bulk update fails.
	 * @param {object[]} lines
	 */
	async removeGiftLinesByKey(lines) {
		const seen = new Set();
		for (const line of lines) {
			if (!line?.key || seen.has(line.key)) continue;
			seen.add(line.key);
			try {
				const resp = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ id: line.key, quantity: 0 }),
				});
				if (resp.ok) {
					this.cartData = await resp.json();
				}
			} catch (err) {
				Logger.error('CartGifts > removeGiftLinesByKey', err);
			}
		}
	}

	applyCartSyncResult() {
		this.gifts.forEach(g => {
			g.setCartContext(this.cartData);
			g.updateCartStatus(this.cartData);
		});
		this.renderBanner();

		const section = window.Shopify.theme.sections.instances.find(s => s.refreshQuickCart);
		if (section) section.refreshQuickCart();
		if (location.pathname === '/cart') location.reload();
	}

	/**
	 * Sync gift SKUs in cart via /cart/update.js call.
	 */
	async syncCart() {
		if (this._syncing) return;

		const updates = {};
		const linesToRemove = [];
		const giftsToAdd = [];

		this.gifts.forEach(g => {
			const lines = g.getGiftCartLines(this.cartData);
			const line = lines[0] || null;
			const shouldHave = g.isEligible(this.cartData) && g.available;

			if (!g.giftVariantId && !g.handle) return;

			if (shouldHave && g.giftVariantId) {
				if (!line || line.quantity !== 1) {
					updates[g.giftVariantId] = 1;
					giftsToAdd.push(g);
				}
				return;
			}

			lines.forEach(l => {
				updates[g.getLineVariantId(l)] = 0;
				linesToRemove.push(l);
			});
		});

		if (!Object.keys(updates).length) return;

		this._syncing = true;
		try {
			const resp = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ updates }),
			});

			if (!resp.ok) {
				const err = await resp.json().catch(() => ({}));
				Logger.error('CartGifts > syncCart > update failed', err);
				if (giftsToAdd.length) {
					giftsToAdd.forEach(g => { g.available = false; });
					this.renderBanner();
				}
				if (linesToRemove.length) {
					await this.removeGiftLinesByKey(linesToRemove);
					this.applyCartSyncResult();
				}
				return;
			}

			this.cartData = await resp.json();
			giftsToAdd.forEach(g => {
				if (!g.getGiftCartLine(this.cartData)) g.available = false;
			});
			this.applyCartSyncResult();
		} finally {
			this._syncing = false;
		}
	}
}

/**
 * Custom element <cart-gifts>
 */
export default class CartGifts extends HTMLElement {
	constructor() {
		super();
		this.logic = new CartGiftsLogic(this);
		this._onCartUpdate = e => this.logic.onCartChange(e.detail?.cart);
		this._onRemoveClick = e => {
			if (e.target.matches('.cart__remove, [data-cart-remove], .cart__quantity input')) {
				this.logic.onCartChange();
			}
		};
	}

	connectedCallback() {
		this.logic.loadConfig();

		if (!this.logic.gwpEnabled) {
			this.style.display = 'none';
		}

		this.logic.init().then(() => {
			if (!this.logic.gwpEnabled) return;

			document.addEventListener('cart:updated', this._onCartUpdate);
			document.addEventListener('cart:refresh', this._onCartUpdate);
			document.addEventListener('click', this._onRemoveClick);
			this._pollInterval = setInterval(() => {
				this.logic._pollStockTick += 1;
				this.logic.onCartChange(null, {
					refreshStock: this.logic._pollStockTick % 15 === 0,
				});
			}, 2000);
		});
	}

	disconnectedCallback() {
		document.removeEventListener('cart:updated', this._onCartUpdate);
		document.removeEventListener('cart:refresh', this._onCartUpdate);
		document.removeEventListener('click', this._onRemoveClick);
		clearInterval(this._pollInterval);
	}

	static get observedAttributes() { return ['cart-subtotal']; }
	attributeChangedCallback(name) { if (name === 'cart-subtotal') this.logic.onCartChange(); }
}

customElements.define('cart-gifts', CartGifts);
